import mqtt_module from 'mqtt'
import { Buffer } from 'buffer'
import { meshLog } from "../utils/spUtils"

class Mqtt {
    constructor(url, auth = {}) {
        this.mqtt = mqtt_module
        this.clientId = this.getGuid32()
        this.reconnectNum = 0
        this.heartbeatTimer = null
        this.isConnected = false
        this.messageCallbacks = [] // 支持多个消息回调
        this.reconnectHandler = null
        this.errorHandler = null
        // 从auth参数中获取用户名和密码
        const { username, password } = auth
        meshLog('username', username);

        const clientId = 'mqttjs_' + Math.random().toString(16).substr(2, 8)
        const options = {
            keepalive: 20,
            clientId: this.clientId + '_web', // 使用统一的 clientId
            protocolId: 'MQTT',
            protocolVersion: 4,
            clean: true,
            reconnectPeriod: 1000,
            connectTimeout: 30 * 1000,
            // 添加用户名和密码
            username: username || null,
            password: password || null,
            will: {
                topic: 'WillMsg',
                payload: 'Connection Closed abnormally..!',
                qos: 0,
                retain: false
            }
        }

        this.client = this.mqtt.connect("wss://" + url + "/mqtt", options)
        this.setupEventListeners()
    }

    // 设置事件监听器
    setupEventListeners() {
        this.reconnectHandler = () => {
            this.reconnectNum++
            if (this.reconnectNum >= 10) {
                meshLog('重连次数超过限制，停止重连')
                this.client.end(true)
            } else {
                meshLog('正在重连... 第' + this.reconnectNum + '次')
            }
        }

        this.errorHandler = (error) => {
            meshLog('MQTT 错误:', error)
        }

        this.client.on('reconnect', this.reconnectHandler)
        this.client.on('error', this.errorHandler)
    }

    // 连接方法
    link() {
        return new Promise((resolve, reject) => {
            const connectTimeout = setTimeout(() => {
                reject(new Error('连接超时'))
            }, 30000)

            const connectHandler = () => {
                clearTimeout(connectTimeout)
                this.isConnected = true
                meshLog('---mqtt链接成功---')

                // 启动心跳
                this.startHeartbeat()

                // 清理一次性监听器
                this.client.removeListener('error', errorHandler)
                resolve(this.client)
            }

            const errorHandler = (error) => {
                clearTimeout(connectTimeout)
                this.client.removeListener('connect', connectHandler)
                reject(error)
            }

            this.client.once('connect', connectHandler)
            this.client.once('error', errorHandler)
        })
    }

    // 订阅主题
    subscribe(topic, options = { qos: 0 }) {
        return new Promise((resolve, reject) => {
            this.client.subscribe(topic, options, (err) => {
                if (err) {
                    meshLog(`订阅 ${topic} 失败:`, err)
                    reject(err)
                } else {
                    meshLog(`---${topic} 订阅成功---`)
                    resolve()
                }
            })
        })
    }

    // 发布消息
    publish(topic, sendMsg, options = { qos: 0, retain: false }) {
        return new Promise((resolve, reject) => {
            const payload = typeof sendMsg === 'object' ? JSON.stringify(sendMsg) : sendMsg

            this.client.publish(topic, payload, options, (err) => {
                if (err) {
                    meshLog(`发布到 ${topic} 失败:`, err)
                    reject(err)
                } else {
                    meshLog(`---${topic} 发送成功---`)
                    resolve()
                }
            })
        })
    }

    // 添加消息监听器
    onMessage(callback) {
        const messageHandler = (topic, message) => {
            try {
                let data = message.toString()
                // 尝试解析 JSON
                if (data.startsWith('{') || data.startsWith('[')) {
                    data = JSON.parse(data)
                }
                callback(data, topic)
            } catch (error) {
                meshLog('消息解析错误:', error)
                callback(message.toString(), topic)
            }
        }

        this.messageCallbacks.push({ callback, handler: messageHandler })
        this.client.on('message', messageHandler)
        return () => this.offMessage(callback)
    }

    // 移除消息监听器
    offMessage(callback) {
        const index = this.messageCallbacks.findIndex(item => item.callback === callback)
        if (index !== -1) {
            const { handler } = this.messageCallbacks[index]
            this.client.removeListener('message', handler)
            this.messageCallbacks.splice(index, 1)
        }
    }

    // 心跳机制
    startHeartbeat() {
        // 清除现有的心跳定时器
        this.stopHeartbeat()

        this.heartbeatTimer = setInterval(() => {
            if (this.client.connected) {
                try {
                    const payload = JSON.stringify({
                        ts: Date.now(),
                        id: this.clientId,
                        type: 'heartbeat'
                    })

                    this.client.publish('client/heartbeat', payload, { qos: 0, retain: false })

                    meshLog(`[MQTT HEARTBEAT] ${new Date().toISOString()} -> ${payload}`)
                } catch (error) {
                    meshLog('心跳发送失败:', error)
                }
            } else {
                meshLog('[MQTT HEARTBEAT] 客户端未连接，跳过发送')
            }
        }, 30000) // 30秒一次
    }

    // 停止心跳
    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer)
            this.heartbeatTimer = null
        }
    }

    // 断开连接
    disconnect() {
        meshLog('---断开 MQTT 连接---')

        // 停止心跳
        this.stopHeartbeat()

        // 清理所有监听器
        if (this.reconnectHandler) {
            this.client.removeListener('reconnect', this.reconnectHandler)
        }

        if (this.errorHandler) {
            this.client.removeListener('error', this.errorHandler)
        }

        // 清理消息监听器
        this.messageCallbacks.forEach(({ handler }) => {
            this.client.removeListener('message', handler)
        })
        this.messageCallbacks = []

        // 断开连接
        this.isConnected = false
        this.client.end(true)
    }

    // 获取连接状态
    getConnectionStatus() {
        return {
            isConnected: this.isConnected,
            clientId: this.clientId,
            reconnectCount: this.reconnectNum
        }
    }

    // 生成 GUID
    getGuid32() {
        let rt_str = String.fromCharCode(65 + Math.floor(Math.random() * 26))
        for (let i = 0; i < 31; ++i) {
            const num = Math.floor(Math.random() * (26 + 26 + 10))
            let ch_str
            if (num < 10) {
                ch_str = num.toString()
            } else if (num < 36) {
                ch_str = String.fromCharCode(65 + num - 10)
            } else {
                ch_str = String.fromCharCode(97 + num - 36)
            }
            rt_str += ch_str
        }
        return rt_str
    }
}

export default Mqtt
